import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import style from "./operating-theatre.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { RootState, useAppSelector } from "../../store";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import Logs from "../../assets/images/logs.png";
import {
  useGetOperatingTheatreListQuery,
  useUpdateOperatingTheatreActivationMutation,
  useGetOperatingTheatreDetailsQuery,
} from "../../services/api";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../components/Dialog";
import { Input } from "../../components/UserInput";
import { updateStatusSchema } from "../../yup";

type FormState = typeof updateStatusSchema.__outputType;
type OperatingTheatreAction =
  | "view"
  | "edit"
  | "logs"
  | "deactivate"
  | "activate";

type QueryParams = {
  search_key?: string;
  health_org_id: number;
  order_by_columns: string;
  order: "Asc" | "Dsc";
  is_detailed: boolean;
};

type OperatingTheatreField = {
  id: string;
  label: string;
  flex?: number;
  sortable?: boolean;
  type?: string;
  render?: (item: OperatingTheatre) => JSX.Element;
};

// Functional component representing the operating theatres page
/**
 * Renders the Operating Theatres listing with search, actions, and activation workflows.
 *
 * Returns:
 *   JSX.Element: Page layout for viewing and managing operating theatres.
 *
 * Notes:
 *   Relies on RTK Query hooks for data and mutations; assumes authenticated user context provides health_org_id.
 */
const OperatingTheatres = () => {
  const { userDetails } = useAppSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [updateOperatingTheatreActivation] =
    useUpdateOperatingTheatreActivationMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedOperatingTheatre, setSelectedOperatingTheatre] =
    useState<OperatingTheatre | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const selectedOperatingTheatreId =
    selectedOperatingTheatre?.id?.toString() ?? "";
  const { data: operatingTheatreDetails } = useGetOperatingTheatreDetailsQuery(
    selectedOperatingTheatreId,
    {
      skip: !logModalVisible || !selectedOperatingTheatreId,
    }
  );

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState<QueryParams>({
    search_key: "",
    health_org_id: userDetails?.health_org_id ?? 0,
    order_by_columns: "created_at",
    order: "Dsc",
    is_detailed: true,
  });

  const formatTheatreLocation = useCallback(
    (theatre: OperatingTheatre) =>
      [theatre?.name, theatre?.room_no, theatre?.floor, theatre?.building]
        .filter(Boolean)
        .join(", "),
    []
  );

  // Centralizes action routing so selection state stays consistent across dialog and navigation flows.
  const handleMenu = useCallback(
    (value: OperatingTheatreAction, operatingTheatre: OperatingTheatre) => {
      setSelectedOperatingTheatre(operatingTheatre);
      if (value === "deactivate" || value === "activate") {
        setModalVisible(true);
        return;
      }
      if (value === "view") {
        navigate(`/operating-theatres/details/${operatingTheatre?.id}`);
        return;
      }
      if (value === "edit") {
        navigate(`/operating-theatres/edit/${operatingTheatre?.id}`);
        return;
      }
      if (value === "logs") {
        setLogModalVisible(true);
      }
    },
    [navigate, setLogModalVisible, setModalVisible, setSelectedOperatingTheatre]
  );

  const fields: OperatingTheatreField[] = useMemo(
    () => [
      {
        id: "name",
        label: "Theatre Location",
        flex: 2.5,
        sortable: true,
        type: "name",
        render: (item: OperatingTheatre) => (
          <Typography
            className={style.cell}
            sx={{
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {formatTheatreLocation(item)}
          </Typography>
        ),
      },
      {
        id: "is_active",
        label: "Status",
        type: "badge",
        flex: 2,
        sortable: true,
        render: (item: OperatingTheatre) => (
          <StatusBadge isActive={item?.is_active ?? false} />
        ),
      },
      {
        id: "logo_s3_key_name",
        label: "Actions",
        type: "action",
        flex: 1,
        render: (item: OperatingTheatre) => (
          <CustomActionMenu<OperatingTheatre, OperatingTheatreAction>
            item={item}
            isActive={item?.is_active ?? false}
            handleChange={handleMenu}
            actions={
              [
                {
                  label: "View",
                  value: "view",
                  icon: <EyeIcon height={24} width={24} />,
                },
                ...(item.is_active
                  ? ([
                    {
                      label: "Edit",
                      value: "edit",
                      icon: <EditIcon height={24} width={24} />,
                    },
                  ] as ActionItem<OperatingTheatreAction>[])
                  : []),
                {
                  label: "Logs",
                  value: "logs",
                  icon: <img src={Logs} alt="Logs" width={24} />,
                },
                {
                  label: item.is_active ? "Deactivate" : "Activate",
                  value: item.is_active ? "deactivate" : "activate",
                  icon: item.is_active ? (
                    <DeactivateIcon height={24} width={24} />
                  ) : (
                    <PowerSettingsNewIcon height={24} width={24} />
                  ),
                },
              ] as ActionItem<OperatingTheatreAction>[]
            }
          />
        ),
      },
    ],
    [formatTheatreLocation, handleMenu]
  );

  const {
    data: operatingTheatreData,
    refetch,
    isFetching,
  } = useGetOperatingTheatreListQuery(queryParam);

  // for searching in the query
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: e.target.value === "" ? undefined : e.target.value,
      }));
    },
    [setQueryParam]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  /**
   * Toggles activation for the selected operating theatre after optional reason capture.
   *
   * Args:
   *   values (FormState): Form values including deactivate_reason when deactivating.
   *
   * Returns:
   *   Promise<void>: Resolves when mutation completes.
   *
   * Raises:
   *   Displays an error message when the activation mutation fails.
   *
   * Notes:
   *   Expects selectedOperatingTheatre to be set before invocation; refetches the list on success.
   */
  // Activation toggle must clear modal/selection and refetch to avoid stale list status.
  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      const id = selectedOperatingTheatre?.id ?? 0;
      const health_org_id = userDetails?.health_org_id ?? 0;
      const result = await updateOperatingTheatreActivation({
        id,
        is_active: !(selectedOperatingTheatre?.is_active ?? false),
        deactivate_reason,
        health_org_id,
      }).unwrap();
      setMessage({
        type: "success",
        text: "Status updated successfully.",
      });
      setIsLoading(false);
      setReasonModalVisible(false);
      setModalVisible(false);
      setSelectedOperatingTheatre(null);
      setValue("deactivate_reason", "");
      clearErrors();
      refetch();
    } catch (error: unknown) {
      setIsLoading(false);
      const parsedError =
        typeof error === "object" && error !== null
          ? (error as { message?: string; data?: { detail?: string } })
          : undefined;
      const errorText =
        parsedError?.message ||
        parsedError?.data?.detail ||
        "Something went wrong!";
      setMessage({
        type: "error",
        text: errorText,
      });
    }
  };

  // Render the Operating Theatre component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={queryParam.search_key ?? ""}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label={"Add New Operating Theatre"}
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<OperatingTheatre>
          fields={fields}
          data={operatingTheatreData?.items || []}
          loading={isFetching}
          onDoubleClick={(operatingTheatre: OperatingTheatre) => {
            navigate(`/operating-theatres/details/${operatingTheatre?.id}`);
          }}
          defaultOrderBy={queryParam.order_by_columns}
          searchTerm={queryParam.search_key}
          onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to ${selectedOperatingTheatre?.is_active ? "deactivate " : "activate "
            }"${selectedOperatingTheatre?.name?.toUpperCase() ?? ""}"?`}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            if (selectedOperatingTheatre?.is_active) {
              setReasonModalVisible(true);
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setSelectedOperatingTheatre(null);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={operatingTheatreDetails || selectedOperatingTheatre}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
};
export default OperatingTheatres;
